import { createSelector } from "reselect";
import { IntlSelectors, uninitState, loadingState, successState, statusSuccess, statusLoading, statusUninit } from "@grandvisionhq/state";
import { convertToLensTypeInfo, lowToHigh, convertToPackageInfo, convertToLensAddOnInfo } from "../../../state/helpers.js";
import { getLensType, getPackage, getAddOn } from "../../../products/state/products/selectors.internal.js";
import { getBundleFrame, getBundleLensTypes, getBundleLensType, getBundlePackages, getBundlePackage, getBundleAddOns } from "./selectors.internal.js";
const isDefined = (v) => typeof v !== "undefined";
const isDefinedAndHasPrice = (v) => isDefined(v) && isDefined(v.price);
const isMasterProduct = (product, sku) => product?.masterVariant.sku === sku;
const isAnyStillLoading = (resources, skus) => {
  return [...skus].reduce((isLoading, sku) => {
    if (isLoading) {
      return isLoading;
    }
    const resource = resources[sku] ?? uninitState();
    return statusLoading(resource) || statusUninit(resource);
  }, false);
};
const getLensTypes = createSelector(
  getBundleFrame,
  getBundleLensTypes,
  IntlSelectors.getLocale,
  (s) => s,
  (bundle, lensTypes, locale, s) => {
    if (!bundle || !locale) {
      return uninitState();
    }
    const { lensTypeSkus } = bundle;
    if (isAnyStillLoading(lensTypes, lensTypeSkus)) {
      return loadingState();
    }
    const results = [...lensTypeSkus].map((lensTypeSku) => {
      const lensType = getLensType(s, { lensTypeSku });
      if (!lensType) {
        return void 0;
      }
      if (!isMasterProduct(lensType, lensTypeSku)) {
        return void 0;
      }
      return convertToLensTypeInfo(lensType, lensTypeSku, locale);
    }).filter(isDefinedAndHasPrice).sort(
      lowToHigh((lensType) => lensType.price?.discountedPrice ?? lensType.price?.originalPrice)
    );
    return successState(results);
  }
);
const getPackages = createSelector(
  getBundleLensType,
  getBundlePackages,
  IntlSelectors.getLocale,
  (s) => s,
  (lensType, packages, locale, s) => {
    if (!lensType || !locale) {
      return uninitState();
    }
    if (!statusSuccess(lensType)) {
      return lensType;
    }
    const { packageSkus } = lensType.result;
    if (isAnyStillLoading(packages, packageSkus)) {
      return loadingState();
    }
    const results = [...packageSkus].map((packageSku) => {
      const packageProduct = getPackage(s, { packageSku });
      if (!packageProduct) {
        return void 0;
      }
      if (!isMasterProduct(packageProduct, packageSku)) {
        return void 0;
      }
      return convertToPackageInfo(packageProduct, packageSku, locale);
    }).filter(isDefinedAndHasPrice).sort(
      lowToHigh(
        (packageInfo) => packageInfo.price?.discountedPrice ?? packageInfo.price?.originalPrice
      )
    );
    return successState(results);
  }
);
const getLensAddOns = createSelector(
  getBundlePackage,
  getBundleAddOns,
  IntlSelectors.getLocale,
  (s) => s,
  (bundlePackage, addOns, locale, s) => {
    if (!bundlePackage || !locale) {
      return uninitState();
    }
    if (!statusSuccess(bundlePackage)) {
      return bundlePackage;
    }
    const { addOnSkus } = bundlePackage.result;
    if (isAnyStillLoading(addOns, addOnSkus)) {
      return loadingState();
    }
    const results = [...addOnSkus].map((addOnSku) => {
      const addOnProduct = getAddOn(s, { addOnSku });
      if (!addOnProduct) {
        return void 0;
      }
      if (!isMasterProduct(addOnProduct, addOnSku)) {
        return void 0;
      }
      return convertToLensAddOnInfo(addOnProduct, addOnSku, locale);
    }).filter(isDefinedAndHasPrice).sort(
      lowToHigh((addOnInfo) => addOnInfo.price?.discountedPrice ?? addOnInfo.price?.originalPrice)
    );
    return successState(results);
  }
);
export {
  getLensAddOns,
  getLensTypes,
  getPackages
};
//# sourceMappingURL=selectors.js.map
